<template>
  <div class="connector-lock" :class="{ 'connector-lock--compact': compact }" role="status" data-e2e="connector-lock">
    <span class="connector-lock__icon" aria-hidden="true">
      <font-awesome-icon :icon="['fas', 'lock']" fixed-width />
    </span>
    <div class="connector-lock__title">This AutoCount module is part of AutoCount Full</div>
    <div class="connector-lock__body" v-if="lock.message">{{ lock.message }}</div>
    <router-link
      v-if="lock.isWorkspaceAdmin"
      :to="settingsRoute"
      class="connector-lock__action"
      data-e2e="connector-lock-action"
    >{{ actionLabel }}</router-link>
    <div class="connector-lock__ask" v-else>{{ askLine }}</div>
  </div>
</template>

<script>
/*
 * The lock card for a module AutoCount Full covers and this workspace's plan does not
 * (docs/billing-autocount-connector-pricing-proposal.md §9 in the SVN repo). Listing, entry and
 * dashboard views show it in place of their red error when a read answers 402
 * connector_full_required (connectorLock.js): the module is fine, it is just not on the plan.
 *
 * The server's message says what is locked and what to do. A workspace admin also gets the way
 * there, Settings › AutoCount, where the trial starts and AutoCount Full is bought; anyone else is
 * told who can. `compact` is the dashboard widget's smaller version.
 */
export default {
  name: 'FTSConnectorLockCard',
  props: {
    // { message, isWorkspaceAdmin, trialAvailable }, from connectorLockFromError.
    lock: { type: Object, required: true },
    compact: Boolean,
  },
  computed: {
    settingsRoute() {
      return { name: 'settings', params: { section: 'autocount' } };
    },
    actionLabel() {
      return this.lock.trialAvailable ? 'Start the free trial' : 'Upgrade to AutoCount Full';
    },
    askLine() {
      return this.lock.trialAvailable
        ? 'Ask a workspace admin to start the free trial.'
        : 'Ask a workspace admin to upgrade to AutoCount Full.';
    },
  },
};
</script>

<style lang="scss" scoped>
/* An expected state with a next step, not an error: neutral ink, the primary colour for the way on. */
.connector-lock {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 440px;
  margin: 3rem auto;
  padding: 0 1rem;
  text-align: center;
  color: var(--fts-text);
}

.connector-lock__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 8px;
  border-radius: 50%;
  background: var(--fts-gray-100);
  color: var(--fts-text-secondary);
  font-size: 1.375rem;
}

.connector-lock__title {
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 600;
}

.connector-lock__body,
.connector-lock__ask {
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--fts-text-secondary);
}

.connector-lock__action {
  margin-top: 8px;
  padding: 6px 14px;
  border-radius: var(--inistate-rounded-4, 4px);
  background: var(--fts-primary);
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;

  &:hover,
  &:focus {
    color: #ffffff;
    text-decoration: none;
    filter: brightness(0.95);
  }
}

/* Inside a dashboard widget: the same card, smaller. */
.connector-lock--compact {
  gap: 4px;
  margin: 0 auto;
  padding: 0.5rem;

  .connector-lock__icon {
    width: 32px;
    height: 32px;
    margin-bottom: 2px;
    font-size: 0.875rem;
  }

  .connector-lock__title {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .connector-lock__body,
  .connector-lock__ask {
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .connector-lock__action {
    margin-top: 4px;
    padding: 3px 10px;
    font-size: 0.75rem;
  }
}
</style>
